import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FragBimViewer, {
  type FragFilter,
  type FragModelInput,
  type IFloorClip,
  type ISyncCamera,
} from "./FragBimViewer/FragBimViewer";
import type { MultiViewerFragFileRef } from "./MultiViewer";
import type { ViewerSyncState } from "./viewerSyncTypes";
import type {
  FragDownloadFileRequest,
  FragDownloadWorkerRequest,
  FragDownloadWorkerResponse,
} from "./fragDownload.worker";

// ---------------------------------------------------------------------------
// Local frag file override — add paths here to bypass cloud loading.
// Leave empty to use cloud files.
// ---------------------------------------------------------------------------
const LOCAL_FRAG_PATHS: string[] = [];

type BlobLoadState =
  | { status: "idle" }
  | { status: "loading"; loadedCount: number; totalCount: number; currentFileName?: string }
  | { status: "failed"; message: string };

type LoadedFragFileInfo = {
  name: string;
  fragSizeBytes?: number;
  manifestSizeBytes?: number;
};

type CachedFragModel = {
  model: FragModelInput;
};

const getCachedModelId = (cached: CachedFragModel): string =>
  cached.model.modelId ?? cached.model.name;

const overlayTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 12,
  lineHeight: 1.4,
  textAlign: "center",
  color: "#868e96",
};

const errorTextStyle: React.CSSProperties = {
  ...overlayTextStyle,
  color: "#e03131",
};

const toFragFileName = (fileName: string): string => {
  const trimmed = fileName.trim();
  if (!trimmed) return "";
  if (trimmed.toLowerCase().endsWith(".frag")) return trimmed;
  if (trimmed.toLowerCase().endsWith(".ifc")) return `${trimmed.slice(0, -4)}.frag`;
  return `${trimmed}.frag`;
};

const getFragCacheKey = (file: MultiViewerFragFileRef): string =>
  [
    file.frag.blobName ?? toFragFileName(file.fileName),
    file.frag.sizeBytes ?? "",
    file.manifest.sizeBytes ?? "",
  ].join(":");

const getFragModelId = (file: MultiViewerFragFileRef): string =>
  [
    toFragFileName(file.fileName),
    file.frag.sizeBytes ?? "",
    file.manifest.sizeBytes ?? "",
  ].join(":");

const formatBytes = (bytes: number | undefined): string => {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 ? 1 : 2)} ${units[unitIndex]}`;
};

const createFragDownloadWorker = () =>
  new Worker(new URL("./fragDownload.worker.ts", import.meta.url), { type: "module" });

interface Viewer3dProps {
  fragFiles: MultiViewerFragFileRef[];
  isLoadingFragFiles?: boolean;
  isFetchingFragFiles?: boolean;
  fragFilesError?: unknown;
  visibleMcbimFileNames: string[];
  clipZMm?: { bottom: number; top: number } | null;
  hideIfcCovering?: boolean;
  syncState: ViewerSyncState;
  onCameraChange: (cameraDirection: [number, number, number], distance3d: number) => void;
}

export const Viewer3d: React.FC<Viewer3dProps> = ({
  fragFiles,
  isLoadingFragFiles = false,
  isFetchingFragFiles = false,
  fragFilesError,
  visibleMcbimFileNames,
  clipZMm,
  hideIfcCovering = true,
  syncState,
  onCameraChange,
}) => {
  const [fileInfoModalOpen, setFileInfoModalOpen] = useState(false);
  const zFloor = (clipZMm?.bottom ?? 0) / 1000;
  const hasLocalFragPaths = LOCAL_FRAG_PATHS.length > 0;
  const modelCacheRef = useRef<Map<string, CachedFragModel>>(new Map());
  const loadedSceneModelIdsRef = useRef<Set<string>>(new Set());
  const [cacheVersion, setCacheVersion] = useState(0);
  const [loadedSceneModelIdsKey, setLoadedSceneModelIdsKey] = useState("");

  const handleLoadedModelIdsChange = useCallback((modelIds: string[]) => {
    const nextIds = [...modelIds].sort();
    const nextKey = nextIds.join("|");
    loadedSceneModelIdsRef.current = new Set(nextIds);
    setLoadedSceneModelIdsKey((current) => current === nextKey ? current : nextKey);
  }, []);

  const canUseCachedModel = useCallback((cached: CachedFragModel | undefined): cached is CachedFragModel => {
    if (!cached) return false;
    if (cached.model.buffer.byteLength > 0) return true;
    return loadedSceneModelIdsRef.current.has(getCachedModelId(cached));
  }, []);

  const floorClip = useMemo<IFloorClip | null>(() => {
    if (clipZMm?.bottom == null || clipZMm?.top == null) return null;
    const bottom = clipZMm.bottom / 1000;
    const top = clipZMm.top / 1000;
    const archTop = bottom + 0.2 * (top - bottom);
    return { bottom, top, archTop };
  }, [clipZMm?.bottom, clipZMm?.top]);
  const fragFilter = useMemo<FragFilter | null>(
    () => hideIfcCovering ? { hiddenIfcTypes: ["IFCCOVERING"] } : null,
    [hideIfcCovering]
  );

  const syncCamera = useMemo<ISyncCamera | undefined>(() => {
    if (!syncState.syncViewports || !syncState.viewPoint2d) return undefined;
    return {
      viewPoint: syncState.viewPoint2d,
      zFloor,
      direction: syncState.cameraDirection,
      distance: syncState.distance3d,
    };
  }, [syncState, zFloor]);

  // Local override — loaded once on mount if LOCAL_FRAG_PATHS is set
  const [localModels, setLocalModels] = useState<FragModelInput[] | null>(null);
  useEffect(() => {
    if (!hasLocalFragPaths) return;
    const paths = LOCAL_FRAG_PATHS;
    let cancelled = false;
    (async () => {
      const models: FragModelInput[] = [];
      for (const path of paths) {
        const res = await fetch(path);
        if (!res.ok) { console.error(`[Viewer3d] local frag fetch failed: ${path}`); continue; }
        models.push({ name: path.split("/").pop() ?? path, buffer: await res.arrayBuffer() });
      }
      if (!cancelled) setLocalModels(models);
    })();
    return () => { cancelled = true; };
  }, [hasLocalFragPaths]);

  const filteredFragFiles = useMemo(() => {
    if (!fragFiles?.length || !visibleMcbimFileNames.length) return [];

    const visibleFragFileNames = new Set(
      visibleMcbimFileNames.map(toFragFileName).filter(Boolean)
    );

    return fragFiles.filter((file) => visibleFragFileNames.has(toFragFileName(file.fileName)));
  }, [fragFiles, visibleMcbimFileNames]);
  const filteredFragFilesKey = useMemo(
    () => filteredFragFiles.map(getFragCacheKey).join("|"),
    [filteredFragFiles]
  );
  const filteredFragFilesFetchKey = useMemo(
    () => filteredFragFiles.map((file) => `${getFragCacheKey(file)}:${file.frag.url}`).join("|"),
    [filteredFragFiles]
  );
  const availableFragFilesKey = useMemo(
    () => fragFiles.map(getFragCacheKey).join("|"),
    [fragFiles]
  );
  const [blobLoadState, setBlobLoadState] = useState<BlobLoadState>({ status: "idle" });

  useEffect(() => {
    if (hasLocalFragPaths) return;
    if (fragFiles.length) {
      const availableKeys = new Set(fragFiles.map(getFragCacheKey));
      let pruned = false;
      for (const key of modelCacheRef.current.keys()) {
        if (!availableKeys.has(key)) {
          modelCacheRef.current.delete(key);
          pruned = true;
        }
      }
      if (pruned) setCacheVersion((version) => version + 1);
    }

    if (!filteredFragFiles.length) { setBlobLoadState({ status: "idle" }); return; }

    const missingFragFiles = filteredFragFiles.filter((file) => {
      const key = getFragCacheKey(file);
      const cached = modelCacheRef.current.get(key);
      return !canUseCachedModel(cached);
    });

    if (!missingFragFiles.length) { setBlobLoadState({ status: "idle" }); return; }

    let cancelled = false;
    let finished = false;
    const requestId = `frag-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const worker = createFragDownloadWorker();
    const pendingModels = new Map<string, CachedFragModel>();
    const workerFiles: FragDownloadFileRequest[] = missingFragFiles.map((file) => ({
      key: getFragCacheKey(file),
      name: toFragFileName(file.fileName),
      modelId: getFragModelId(file),
      url: file.frag.url,
    }));

    setBlobLoadState({ status: "loading", loadedCount: 0, totalCount: missingFragFiles.length });

    worker.onmessage = (event: MessageEvent<FragDownloadWorkerResponse>) => {
      if (cancelled || event.data.requestId !== requestId) return;

      if (event.data.type === "progress") {
        setBlobLoadState({
          status: "loading",
          loadedCount: event.data.loadedCount,
          totalCount: event.data.totalCount,
          currentFileName: event.data.currentFileName,
        });
        return;
      }

      if (event.data.type === "loaded") {
        pendingModels.set(event.data.key, {
          model: {
            name: event.data.name,
            modelId: event.data.modelId,
            buffer: event.data.buffer,
          },
        });
        setBlobLoadState((prev) => {
          if (prev.status !== "loading") return prev;
          return {
            status: "loading",
            loadedCount: Math.min(prev.loadedCount + 1, prev.totalCount),
            totalCount: prev.totalCount,
            currentFileName: prev.currentFileName,
          };
        });
        return;
      }

      if (event.data.type === "done") {
        finished = true;
        for (const [key, value] of pendingModels) {
          modelCacheRef.current.set(key, value);
        }
        setCacheVersion((version) => version + 1);
        setBlobLoadState({ status: "idle" });
        worker.terminate();
        return;
      }

      if (event.data.type === "failed") {
        finished = true;
        setBlobLoadState({ status: "failed", message: event.data.message });
        worker.terminate();
      }
    };

    worker.onerror = (event) => {
      if (cancelled) return;
      finished = true;
      setBlobLoadState({ status: "failed", message: event.message || "Kunne inte ladda frag-filer." });
      worker.terminate();
    };

    worker.postMessage({
      type: "load",
      requestId,
      files: workerFiles,
    } satisfies FragDownloadWorkerRequest);

    return () => {
      cancelled = true;
      if (!finished) {
        worker.postMessage({
          type: "cancel",
          requestId,
        } satisfies FragDownloadWorkerRequest);
      }
      worker.terminate();
    };
  }, [
    availableFragFilesKey,
    canUseCachedModel,
    filteredFragFilesFetchKey,
    hasLocalFragPaths,
    loadedSceneModelIdsKey,
  ]);

  const cloudModels = useMemo(() => {
    if (!filteredFragFiles.length) return [];

    return filteredFragFiles
      .map((file) => modelCacheRef.current.get(getFragCacheKey(file)))
      .filter(canUseCachedModel)
      .map((cached) => cached.model);
  }, [cacheVersion, canUseCachedModel, filteredFragFilesKey, loadedSceneModelIdsKey]);

  const activeModels = localModels ?? (cloudModels.length ? cloudModels : null);
  const loadedFileInfo = useMemo<LoadedFragFileInfo[]>(() => {
    if (!activeModels) return [];

    const fileInfoByFragName = new Map(
      filteredFragFiles.map((file) => [
        toFragFileName(file.fileName),
        {
          name: toFragFileName(file.fileName),
          fragSizeBytes: file.frag.sizeBytes,
          manifestSizeBytes: file.manifest.sizeBytes,
        },
      ])
    );

    return activeModels
      .map((model) => {
        const fragName = toFragFileName(model.name);
        return fileInfoByFragName.get(fragName) ?? { name: model.name };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [activeModels, filteredFragFiles]);

  const overlay = useMemo(() => {
    if (activeModels) return null;
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          {(isLoadingFragFiles || isFetchingFragFiles || blobLoadState.status === "loading") && (
            <div
              aria-label="Laddar"
              style={{
                width: 14,
                height: 14,
                border: "2px solid rgba(134, 142, 150, 0.28)",
                borderTopColor: "#868e96",
                borderRadius: "50%",
                animation: "multiviewer-spin 0.8s linear infinite",
              }}
            />
          )}
          {fragFilesError ? (
            <p style={errorTextStyle}>Kunde inte hämta frag-filer.</p>
          ) : blobLoadState.status === "failed" ? (
            <p style={errorTextStyle}>{blobLoadState.message}</p>
          ) : blobLoadState.status === "loading" ? (
            <p style={overlayTextStyle}>
              Laddar {blobLoadState.loadedCount}/{blobLoadState.totalCount} frag-filer…
              {blobLoadState.currentFileName ? <><br />{blobLoadState.currentFileName}</> : null}
            </p>
          ) : (
            <p style={overlayTextStyle}>Inga frag-filer hittades för projektet.</p>
          )}
        </div>
      </div>
    );
  }, [activeModels, blobLoadState, fragFilesError, isFetchingFragFiles, isLoadingFragFiles]);

  return (
    <div style={{ height: "100%", width: "100%", overflow: "hidden", position: "relative" }}>
      <style>
        {"@keyframes multiviewer-spin { to { transform: rotate(360deg); } }"}
      </style>
      {activeModels ? (
        <>
          <FragBimViewer
            models={activeModels}
            filter={fragFilter}
            syncCamera={syncCamera}
            floorClip={floorClip}
            onCameraChange={onCameraChange}
            onLoadedModelIdsChange={handleLoadedModelIdsChange}
          />
          {blobLoadState.status === "loading" && (
            <div
              style={{
                position: "absolute",
                right: 8,
                bottom: 8,
                zIndex: 20,
                borderRadius: 4,
                background: "rgba(255,255,255,0.82)",
                color: "#495057",
                fontSize: 11,
                lineHeight: 1.4,
                border: "1px solid rgba(222, 226, 230, 0.8)",
                padding: "3px 8px",
                pointerEvents: "none",
              }}
            >
              Laddar {blobLoadState.loadedCount}/{blobLoadState.totalCount} nya frag-filer…
              {blobLoadState.currentFileName ? ` ${blobLoadState.currentFileName}` : ""}
            </div>
          )}
          <button
            type="button"
            onClick={() => setFileInfoModalOpen(true)}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 20,
              borderRadius: 4,
              background: "rgba(255,255,255,0.72)",
              color: "#495057",
              fontSize: 11,
              lineHeight: 1.4,
              border: "1px solid rgba(222, 226, 230, 0.8)",
              padding: "3px 8px",
              cursor: "pointer",
              pointerEvents: "auto",
              userSelect: "none",
            }}
          >
            {loadedFileInfo.length} frag-filer
          </button>
          {fileInfoModalOpen && (
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="multiviewer-file-info-title"
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1000,
                background: "rgba(15, 23, 42, 0.36)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setFileInfoModalOpen(false);
              }}
            >
              <div
                style={{
                  width: "min(720px, 100%)",
                  maxHeight: "min(620px, 100%)",
                  overflow: "auto",
                  background: "white",
                  borderRadius: 8,
                  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.26)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "16px 18px",
                    borderBottom: "1px solid #e9ecef",
                  }}
                >
                  <h2 id="multiviewer-file-info-title" style={{ margin: 0, fontSize: 18 }}>
                    Laddade 3D-filer
                  </h2>
                  <button
                    type="button"
                    aria-label="Stäng"
                    onClick={() => setFileInfoModalOpen(false)}
                    style={{
                      width: 30,
                      height: 30,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      background: "white",
                      cursor: "pointer",
                      fontSize: 20,
                      lineHeight: "24px",
                    }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ padding: 18 }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      border: "1px solid #dee2e6",
                      fontSize: 14,
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #dee2e6" }}>Fil</th>
                        <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #dee2e6" }}>Frag</th>
                        <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #dee2e6" }}>Manifest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadedFileInfo.map((file, index) => (
                        <tr key={file.name} style={{ background: index % 2 === 1 ? "#f8f9fa" : "white" }}>
                          <td style={{ padding: 10, borderTop: "1px solid #edf2f7" }}>{file.name}</td>
                          <td style={{ padding: 10, borderTop: "1px solid #edf2f7" }}>{formatBytes(file.fragSizeBytes)}</td>
                          <td style={{ padding: 10, borderTop: "1px solid #edf2f7" }}>{formatBytes(file.manifestSizeBytes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        overlay
      )}
    </div>
  );
};
